/**
 * Session Validation Status Component
 * Shows current session validation configuration and status
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';
import sessionValidator from '../utils/sessionValidator';

const SessionValidationStatus = () => {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState({
    isActive: false,
    frequency: 0,
    cacheInfo: null,
    lastValidation: null
  });

  const updateStatus = () => {
    const isActive = sessionValidator.isValidationActive();
    const frequency = sessionValidator.getValidationFrequency();
    
    // Get cache info (this would need to be exposed from authService for debugging)
    const cacheInfo = {
      hasCachedResult: authService.lastValidationResult !== null,
      cacheTime: authService.lastValidationTime,
      cacheAge: authService.lastValidationTime ? Date.now() - authService.lastValidationTime : 0
    };

    setStatus({
      isActive,
      frequency,
      cacheInfo,
      lastValidation: new Date().toLocaleTimeString()
    });
  };

  useEffect(() => {
    updateStatus();
    const interval = setInterval(updateStatus, 1000); // Update every second
    return () => clearInterval(interval);
  }, []);

  const handleClearCache = () => {
    if (authService.clearValidationCache) {
      authService.clearValidationCache();
    } else {
      // Fallback - manually clear cache properties
      authService.lastValidationTime = 0;
      authService.lastValidationResult = null;
    }
    updateStatus();
  };

  const handleForceValidation = async () => {
    try {
      const result = await authService.validateSession(true); // Force validation
      console.log('Forced validation result:', result);
      updateStatus();
    } catch (error) {
      console.error('Force validation error:', error);
    }
  };

  return (
    <div style={{
      padding: '15px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      margin: '10px 0',
      backgroundColor: '#f8f9fa',
      fontFamily: 'monospace',
      fontSize: '12px'
    }}>
      <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>Session Validation Status</h4>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Authentication:</strong> {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Periodic Validation:</strong> {status.isActive ? '✅ Active' : '❌ Inactive'}
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Validation Frequency:</strong> {Math.floor(status.frequency / 1000 / 60)} minutes
      </div>
      
      <div style={{ marginBottom: '10px' }}>
        <strong>Cache Status:</strong>
        <div style={{ marginLeft: '15px' }}>
          <div>Has Cached Result: {status.cacheInfo?.hasCachedResult ? '✅ Yes' : '❌ No'}</div>
          {status.cacheInfo?.hasCachedResult && (
            <>
              <div>Cache Age: {Math.floor(status.cacheInfo.cacheAge / 1000)}s</div>
              <div>Valid Until: {Math.max(0, 30 - Math.floor(status.cacheInfo.cacheAge / 1000))}s remaining</div>
            </>
          )}
        </div>
      </div>
      
      <div style={{ marginBottom: '15px' }}>
        <strong>Last Update:</strong> {status.lastValidation}
      </div>
      
      <div>
        <button 
          onClick={handleClearCache}
          style={{
            padding: '5px 10px',
            marginRight: '10px',
            backgroundColor: '#ffc107',
            color: 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px'
          }}
        >
          Clear Cache
        </button>
        
        <button 
          onClick={handleForceValidation}
          style={{
            padding: '5px 10px',
            backgroundColor: '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '11px'
          }}
        >
          Force Validation
        </button>
      </div>
      
      <div style={{ marginTop: '10px', fontSize: '11px', color: '#666' }}>
        <strong>Optimizations:</strong>
        <ul style={{ margin: '5px 0', paddingLeft: '15px' }}>
          <li>30-second cache prevents duplicate API calls</li>
          <li>Pending validation deduplication</li>
          <li>10-minute periodic validation interval</li>
          <li>Cache cleared on login/logout</li>
        </ul>
      </div>
    </div>
  );
};

export default SessionValidationStatus;
